import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useBusy } from "../../context/BusyContext";
import { useFormat } from "../../context/FormatContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";
import {
  calculateProductPrices,
  getActivePriceValue,
} from "../../utils/PriceCalculator";
import "../../styles/priceUpdater.css";

interface DropdownOption {
  id: number;
  label: string;
}

interface VendorOption {
  id: number;
  name: string;
  region_id: number | null;
  region_name: string | null;
  status?: string;
}

interface Product {
  id: number;
  vendor_id?: number | null;
  vendorName?: string;
  region_id?: number | null;
  region_name?: string | null;

  SKU: string;
  barCode?: string;
  product_condition: string;
  productName: string;
  activePrice: number | string;
  active_price: number;
  cost: number;
  total_sold: number;
  view_count: number;
  is_featured: number;
  is_promoted: number;
  stock_status: string;
  status: string;
  created_at: string;

  categoryId: number;
  subcategoryId: number;
  productType?: number;

  brand_id?: number | string | null;
  brand_name?: string;
  brand_image?: string | null;

  category_name: string;
  subcategory_name: string;
  product_type: string;

  shortDescription?: string;
  longDescription?: string;
  model?: string;
  productVideo?: any;
  colorVariants?: any[];

  discountVoucherIds?: number[];
  discountVouchers?: any[];

  image_path: string;
  images?: any[];
}

interface DiscountVoucherOption {
  id: number;
  title: string;
  code: string;
  discount_type: "fixed" | "percent";
  discount_value: number;
}

export default function Products() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();
  const { formatDateToDMY, formatCurrency } = useFormat();

  const [priceUpdaterOpen, setPriceUpdaterOpen] = useState(false);
  const [priceUpdaterRows, setPriceUpdaterRows] = useState<any[]>([]);
  const [priceUpdaterSaving, setPriceUpdaterSaving] = useState(false);
  const [focusedPriceInputIndex, setFocusedPriceInputIndex] = useState<
    number | null
  >(null);
  const [priceUpdaterFilteredRows, setPriceUpdaterFilteredRows] = useState<
    any[]
  >([]);
  const [isPriceUpdaterFiltered, setIsPriceUpdaterFiltered] = useState(false);

  const priceUpdaterRefs = useRef<
    Array<HTMLInputElement | HTMLSelectElement | null>
  >([]);

  const [data, setData] = useState<Product[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const [categories, setCategories] = useState<DropdownOption[]>([]);
  const [types, setTypes] = useState<DropdownOption[]>([]);
  const [brands, setBrands] = useState<DropdownOption[]>([]);
  const [subcategories, setSubcategories] = useState<DropdownOption[]>([]);
  const [discountVouchers, setDiscountVouchers] = useState<
    DiscountVoucherOption[]
  >([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string>(
    "",
  );

  /* =========================
     HELPERS
  ========================= */

  const formatCostInput = (value: any) => {
    const numberValue = Number(String(value || "").replace(/,/g, ""));

    if (!Number.isFinite(numberValue)) return "";

    return numberValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePriceUpdaterFilterChange = useCallback(
    (filteredRows: any[], hasActiveFilters: boolean) => {
      setPriceUpdaterFilteredRows(filteredRows);
      setIsPriceUpdaterFiltered(hasActiveFilters);
    },
    [],
  );

  const handlePriceUpdaterCostChange = (rowIndex: number, value: string) => {
    setPriceUpdaterRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) return row;

        const calculated = calculateProductPrices(value);

        return {
          ...row,
          cost: calculated.cost,
          actualPrice: calculated.actualPrice,
          previousPrice: calculated.previousPrice,
          sellingPrice: calculated.sellingPrice,
        };
      }),
    );
  };

  const openPriceUpdater = () => {
    priceUpdaterRefs.current = [];

    const rows = data.map((product) => ({
      id: product.id,
      productName: product.productName,
      category_name: product.category_name,
      subcategory_name: product.subcategory_name,
      cost: product.cost ?? 0,
      actualPrice: (product as any).actualPrice ?? 0,
      previousPrice: (product as any).previousPrice ?? 0,
      sellingPrice: (product as any).sellingPrice ?? 0,
      activePrice: product.activePrice || "selling",
    }));

    setPriceUpdaterRows(rows);
    setPriceUpdaterFilteredRows(rows);
    setIsPriceUpdaterFiltered(false);
    setPriceUpdaterOpen(true);

    setTimeout(() => {
      priceUpdaterRefs.current[0]?.focus();
    }, 100);
  };

  const closePriceUpdater = () => {
    setPriceUpdaterOpen(false);
    setPriceUpdaterRows([]);
    setPriceUpdaterFilteredRows([]);
    setIsPriceUpdaterFiltered(false);
  };

  const handlePriceUpdaterActivePriceChange = (
    rowIndex: number,
    value: string,
  ) => {
    setPriceUpdaterRows((prev) =>
      prev.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              activePrice: value,
            }
          : row,
      ),
    );
  };

  const handlePriceUpdaterKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    controlIndex: number,
  ) => {
    if (event.key !== "Enter" && event.key !== "Tab") return;

    event.preventDefault();

    const nextIndex = event.shiftKey ? controlIndex - 1 : controlIndex + 1;

    priceUpdaterRefs.current[nextIndex]?.focus();
  };

  const savePriceUpdater = async () => {
    try {
      setPriceUpdaterSaving(true);

      const rowsToSave =
        isPriceUpdaterFiltered && priceUpdaterFilteredRows.length > 0
          ? priceUpdaterFilteredRows
          : priceUpdaterRows;

      const payload = rowsToSave.map((row) => ({
        id: row.id,
        cost: row.cost,
        activePrice: row.activePrice,
      }));

      const res = await runBusy(() =>
        fetch(`${BASE}/admin/products/price-updater`, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            products: payload,
          }),
        }),
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to update product prices.",
          confirmText: "OK",
        });
        return;
      }

      await fetchProducts();
      closePriceUpdater();

      showModal({
        type: "success",
        title: "Success",
        message: result.message || "Product prices updated successfully.",
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Price updater error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: error?.message || "Failed to update product prices.",
        confirmText: "OK",
      });
    } finally {
      setPriceUpdaterSaving(false);
    }
  };

  const enrichProductRow = (row: any) => {
    const matchedVendor = vendors.find(
      (v) => String(v.id) === String(row?.vendor_id),
    );

    return {
      ...row,
      region_id: matchedVendor?.region_id ?? row?.region_id ?? "",
      region_name: matchedVendor?.region_name ?? row?.region_name ?? "",
    };
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("admin_user") || "null");
    setCurrentUser(user);
  }, []);

  const isVendorUser = Number(currentUser?.group_id) === 3;

  /* =========================
     FORM FIELDS
  ========================= */

  const productFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [{ key: "id", label: "ID", colSpan: 1 }];

    // ✅ Show ONLY for Admin (not Vendor)
    if (!isVendorUser) {
      fields.push(
        {
          key: "vendor_id",
          label: "Vendor",
          type: "select",
          options: vendors
            .filter((v) => v.status !== "inactive")
            .map((v) => ({
              label: v.name,
              value: v.id,
              region_id: v.region_id,
              region_name: v.region_name,
            })),
          colSpan: 2,
        },
        {
          key: "region_name",
          label: "Region",
          type: "text",
          disabled: true,
          colSpan: 2,
        },
      );
    }

    // ✅ Remaining fields
    fields.push(
      { key: "SKU", label: "SKU", colSpan: 1 },
      { key: "barCode", label: "BarCode", colSpan: 1 },
      { key: "productName", label: "Product Name", colSpan: 1 },

      {
        key: "product_condition",
        label: "Condition",
        type: "select",
        options: [
          { label: "New", value: "new" },
          { label: "Used", value: "used" },
        ],
        colSpan: 2,
      },

      {
        key: "shortDescription",
        label: "Short Description",
        type: "text",
        colSpan: 1,
      },

      {
        key: "categoryId",
        label: "Category",
        type: "select",
        options: categories.map((c) => ({
          label: c.label,
          value: c.id,
        })),
        colSpan: 1,
      },

      {
        key: "subcategoryId",
        label: "Subcategory",
        type: "select",
        options: subcategories.map((s) => ({
          label: s.label,
          value: s.id,
        })),
        colSpan: 1,
      },

      {
        key: "productType",
        label: "Product Type",
        type: "select",
        options: types.map((t) => ({
          label: t.label,
          value: t.id,
        })),
        colSpan: 2,
      },

      {
        key: "brand_id",
        label: "Brand",
        type: "select",
        options: [
          { label: "No Brand", value: "" },
          ...brands.map((b) => ({
            label: b.label,
            value: b.id,
          })),
        ],
        colSpan: 2,
      },

      {
        key: "discountVoucherIds",
        label: "Discount Vouchers",
        type: "multiselect",
        options: discountVouchers.map((voucher) => ({
          label: `${voucher.title} (${voucher.code}) - ${
            voucher.discount_type === "percent"
              ? `${voucher.discount_value}%`
              : formatCurrency(voucher.discount_value)
          }`,
          value: voucher.id,
        })),
        colSpan: 2,
      },

      {
        key: "colorVariants",
        label: "Product Colors",
        type: "color-variants",
        colSpan: 2,
      },

      { key: "model", label: "Model", colSpan: 2 },

      { key: "cost", label: "Cost", type: "currency", colSpan: 2 },

      {
        key: "actualPrice",
        label: "Actual Price",
        type: "currency",
        colSpan: 2,
        disabled: true,
      },

      {
        key: "previousPrice",
        label: "Previous Price",
        type: "currency",
        colSpan: 2,
        disabled: true,
      },

      {
        key: "sellingPrice",
        label: "Selling Price",
        type: "currency",
        colSpan: 2,
        disabled: true,
      },

      {
        key: "activePrice",
        label: "Active Price",
        type: "select",
        options: [
          { label: "Actual Price", value: "actual" },
          { label: "Previous Price", value: "previous" },
          { label: "Selling Price", value: "selling" },
        ],
        colSpan: 2,
      },

      {
        key: "is_featured",
        label: "Featured",
        type: "select",
        options: [
          { label: "Yes", value: 1 },
          { label: "No", value: 0 },
        ],
        colSpan: 1,
      },

      {
        key: "is_promoted",
        label: "Promoted",
        type: "select",
        options: [
          { label: "Yes", value: 1 },
          { label: "No", value: 0 },
        ],
        colSpan: 1,
      },

      {
        key: "longDescription",
        label: "Long Description",
        type: "textarea",
        colSpan: 2,
      },

      {
        key: "productVideo",
        label: "Product Video",
        type: "video",
        colSpan: 2,
      },

      {
        key: "stock_status",
        label: "Stock Status",
        type: "select",
        options: [
          { label: "In Stock", value: "in-stock" },
          { label: "Sold Out", value: "soldout" },
        ],
        colSpan: 2,
      },

      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    );

    return fields;
  }, [
    categories,
    subcategories,
    types,
    brands,
    vendors,
    discountVouchers,
    isVendorUser,
  ]);

  /* =========================
     COLUMNS
  ========================= */

  const columns: Column[] = [
    { key: "id", label: "ID" },
    {
      key: "image_path",
      label: "Image",
      imageFolder: "products",
    },
    { key: "productName", label: "Product Name" },
    { key: "region_name", label: "Region" },
    { key: "vendorName", label: "Vendor" },
    { key: "SKU", label: "SKU" },
    { key: "barCode", label: "BarCode" },
    { key: "category_name", label: "Category" },
    { key: "subcategory_name", label: "Subcategory" },
    { key: "product_type", label: "Type" },
    {
      key: "brand_name",
      label: "Brand",
      render: (row: Product) => row.brand_name || "-",
    },
    {
      key: "active_price",
      label: "Active Price",
      render: (row: Product) => formatCurrency(row.active_price),
    },
    {
      key: "cost",
      label: "Cost",
      render: (row: Product) => {
        return formatCurrency(row.cost);
      },
    },
    { key: "total_sold", label: "Sold" },
    { key: "view_count", label: "Views" },
    {
      key: "is_featured",
      label: "Featured",
      render: (row: Product) => (
        <span className={row.is_featured ? "badge-success" : "badge-danger"}>
          {row.is_featured ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "is_promoted",
      label: "Promoted",
      render: (row: Product) => (
        <span className={row.is_promoted ? "badge-success" : "badge-danger"}>
          {row.is_promoted ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "stock_status",
      label: "Stock Status",
      render: (row: Product) => (
        <span
          className={
            row.stock_status === "in-stock" ? "badge-success" : "badge-danger"
          }
        >
          {row.stock_status.charAt(0).toUpperCase() + row.stock_status.slice(1)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row: Product) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (row: any) => formatDateToDMY(row.created_at),
    },
  ];

  /* =========================
     FETCH PRODUCTS
  ========================= */

  const fetchProducts = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE}/admin/products`, {
          credentials: "include",
        }),
      );

      const result = await res.json();
      const freshData = result.data || [];

      setData(freshData);

      setSelectedRow((prev: any) =>
        prev
          ? freshData.find((item: any) => item.id === prev.id) || null
          : null,
      );

      setActiveRow((prev: any) =>
        prev
          ? freshData.find((item: any) => item.id === prev.id) || null
          : null,
      );
    } catch (error) {
      console.error("Fetch products error:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  /* =========================
     FETCH DROPDOWNS
  ========================= */
  const vendorRequest = isVendorUser
    ? Promise.resolve({ data: [] })
    : fetch(`${BASE}/admin/vendors`, { credentials: "include" }).then((r) =>
        r.json(),
      );

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/admin/categories`, { credentials: "include" }).then((r) =>
        r.json(),
      ),
      fetch(`${BASE}/admin/types`, { credentials: "include" }).then((r) =>
        r.json(),
      ),
      fetch(`${BASE}/admin/brands`, { credentials: "include" }).then((r) =>
        r.json(),
      ),
      vendorRequest,
      fetch(`${BASE}/admin/discount-vouchers/active`, {
        credentials: "include",
      }).then((r) => r.json()),
    ]).then(([catRes, typeRes, brandRes, vendorRes, voucherRes]) => {
      setCategories(
        (catRes.data || []).map((c: any) => ({
          id: c.id,
          label: c.category_name,
        })),
      );

      setTypes(
        (typeRes.data || []).map((t: any) => ({
          id: t.id,
          label: t.description,
        })),
      );

      setBrands(
        (brandRes.data || [])
          .filter((b: any) => b.status !== "inactive")
          .map((b: any) => ({
            id: b.id,
            label: b.name,
          })),
      );

      const vendorRows = Array.isArray(vendorRes?.data)
        ? vendorRes.data
        : Array.isArray(vendorRes)
          ? vendorRes
          : [];

      const voucherRows = Array.isArray(voucherRes?.data)
        ? voucherRes.data
        : Array.isArray(voucherRes)
          ? voucherRes
          : [];

      setVendors(
        vendorRows.map((v: any) => ({
          id: v.id,
          name: v.name,
          region_id: v.region_id ?? null,
          region_name: v.region_name ?? "",
          status: v.status,
        })),
      );

      setDiscountVouchers(voucherRows);

      setSubcategories([]);
    });
  }, []);

  const fetchSubcategoriesByCategory = useCallback(
    async (categoryId: number | string) => {
      if (!categoryId) {
        setSubcategories([]);
        return;
      }

      try {
        const res = await fetch(
          `${BASE}/admin/subcategories?categoryId=${categoryId}`,
          { credentials: "include" },
        );

        const result = await res.json();

        setSubcategories(
          (result.data || []).map((s: any) => ({
            id: s.id,
            label: s.name,
          })),
        );
      } catch (error) {
        console.error("Failed to fetch subcategories:", error);
        setSubcategories([]);
      }
    },
    [BASE],
  );

  const handleProductFormChange = useCallback(
    (updatedFormData: any) => {
      const newCategoryId = updatedFormData.categoryId || "";

      if (String(newCategoryId) !== String(selectedCategoryId)) {
        setSelectedCategoryId(newCategoryId);
        fetchSubcategoriesByCategory(newCategoryId);
        return;
      }

      if (!newCategoryId && subcategories.length > 0) {
        setSubcategories([]);
      }
    },
    [selectedCategoryId, fetchSubcategoriesByCategory, subcategories.length],
  );

  /* =========================
     BUILD FORM DATA
  ========================= */

  const buildFormData = (
    formData: any,
    fields: FormField[],
    activeRow?: any,
  ) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.key === "region_name") {
        return;
      }

      if (field.type === "image" && field.imageType === "product") {
        if (Array.isArray(value)) {
          value.forEach((img: any, index: number) => {
            if (img?.file) {
              form.append(field.uploadField || "productImages", img.file);
            }

            if (!img && activeRow?.images?.[index]?.id) {
              form.append("removeImages[]", activeRow.images[index].id);
            }
          });
        }
      } else if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value === "") {
          form.append(field.key, "REMOVE");
        }
      } else if (field.type === "color-variants") {
        const variants = formData[field.key] || [];

        form.append(
          "colorVariants",
          JSON.stringify(
            variants.map((v: any) => ({
              color: v.color,
              existingImages: (v.images || [])
                .filter((img: any) => img && !img.file && img.image_path)
                .map((img: any, index: number) => ({
                  id: img.id || null,
                  image_path: img.image_path,
                  sort_order: index,
                })),
            })),
          ),
        );

        variants.forEach((variant: any, i: number) => {
          (variant.images || []).forEach((img: any, j: number) => {
            if (img?.file) {
              form.append(`colorImage_${i}_${j}`, img.file);
            }
          });
        });
      } else if (field.type === "video") {
        if (value?.file) {
          form.append("productVideo", value.file);
        } else if (value === "") {
          form.append("productVideo", "REMOVE");
        } else if (value?.fileName) {
          form.append("existingProductVideo", value.fileName);
        } else if (field.key === "discountVoucherIds") {
          const ids = Array.isArray(value) ? value : value ? [value] : [];
          form.append("discountVoucherIds", JSON.stringify(ids));
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    // ✅ Always append vendor_id manually
    if (formData.vendor_id !== undefined && formData.vendor_id !== null) {
      form.set("vendor_id", String(formData.vendor_id));
    }

    return form;
  };

  /* =========================
     HANDLE SUBMIT
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      if (isVendorUser) {
        formData.vendor_id = currentUser.id;
      } else {
        const selectedVendorId = formData.vendor_id;

        if (
          selectedVendorId === undefined ||
          selectedVendorId === null ||
          String(selectedVendorId).trim() === ""
        ) {
          showModal({
            type: "error",
            title: "Vendor Required",
            message: "Please select a vendor before saving the product.",
            confirmText: "OK",
          });
          return;
        }
      }

      const form = buildFormData(formData, productFields, activeRow);

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/products`
          : `${BASE}/admin/products/${activeRow.id}`;

      const res = await runBusy(() =>
        fetch(url, {
          method,
          credentials: "include",
          body: form,
        }),
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message:
            result.message ||
            (modalMode === "add"
              ? "Failed to add product."
              : "Failed to update product."),
          confirmText: "OK",
        });
        return;
      }

      fetchProducts();
      setModalOpen(false);
      setActiveRow(null);

      showModal({
        type: "success",
        title: "Success",
        message:
          result.message ||
          (modalMode === "add"
            ? "Product added successfully."
            : "Product updated successfully."),
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Product submit error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.message ||
          (modalMode === "add"
            ? "Failed to add product."
            : "Failed to update product."),
        confirmText: "OK",
      });
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Product",
      message: "Are you sure you want to delete this product?",
      confirmText: "Delete",
      onConfirm: async () => {
        await fetch(`${BASE}/admin/products/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });
        fetchProducts();
      },
    });
  };

  /* =========================
     PRICE UPDATER COLUMNS
  ========================= */

  const priceUpdaterColumns: Column[] = useMemo(
    () => [
      {
        key: "id",
        label: "ID",
        filterable: true,
      },
      {
        key: "productName",
        label: "Product Name",
      },
      {
        key: "category_name",
        label: "Category",
      },
      {
        key: "subcategory_name",
        label: "Subcategory",
      },
      {
        key: "cost",
        label: "Cost",
        filterable: false,
        render: (row: any, visibleRowIndex: number) => {
          const realRowIndex = priceUpdaterRows.findIndex(
            (item) => Number(item.id) === Number(row.id),
          );

          if (realRowIndex < 0) return "-";

          const costControlIndex = visibleRowIndex * 2;

          return (
            <div className="price-updater-input-wrap">
              <input
                ref={(element) => {
                  priceUpdaterRefs.current[costControlIndex] = element;
                }}
                type="text"
                inputMode="decimal"
                className="price-updater-cost-input"
                value={
                  focusedPriceInputIndex === costControlIndex
                    ? row.cost
                    : formatCostInput(row.cost)
                }
                onFocus={() => setFocusedPriceInputIndex(costControlIndex)}
                onBlur={() => setFocusedPriceInputIndex(null)}
                onChange={(event) =>
                  handlePriceUpdaterCostChange(realRowIndex, event.target.value)
                }
                onKeyDown={(event) =>
                  handlePriceUpdaterKeyDown(event, costControlIndex)
                }
              />

              <div className="price-updater-cost-preview">
                {formatCurrency(row.cost)}
              </div>
            </div>
          );
        },
      },
      {
        key: "activePrice",
        label: "Active Price",
        filterable: false,
        render: (row: any, visibleRowIndex: number) => {
          const realRowIndex = priceUpdaterRows.findIndex(
            (item) => Number(item.id) === Number(row.id),
          );

          if (realRowIndex < 0) return "-";

          const activePriceControlIndex = visibleRowIndex * 2 + 1;

          return (
            <select
              ref={(element) => {
                priceUpdaterRefs.current[activePriceControlIndex] = element;
              }}
              className="price-updater-active-select"
              value={row.activePrice}
              onChange={(event) =>
                handlePriceUpdaterActivePriceChange(
                  realRowIndex,
                  event.target.value,
                )
              }
              onKeyDown={(event) =>
                handlePriceUpdaterKeyDown(event, activePriceControlIndex)
              }
            >
              <option value="actual">
                Actual Price ({formatCurrency(row.actualPrice)})
              </option>

              <option value="previous">
                Previous Price ({formatCurrency(row.previousPrice)})
              </option>

              <option value="selling">
                Selling Price ({formatCurrency(row.sellingPrice)})
              </option>
            </select>
          );
        },
      },
      {
        key: "calculated_price",
        label: "Calculated Price",
        filterable: false,
        render: (row: any) => (
          <span className="price-updater-calculated-price">
            {formatCurrency(getActivePriceValue(row))}
          </span>
        ),
      },
    ],
    [
      priceUpdaterRows,
      focusedPriceInputIndex,
      formatCurrency,
      handlePriceUpdaterCostChange,
      handlePriceUpdaterActivePriceChange,
      handlePriceUpdaterKeyDown,
    ],
  );

  /* =========================
     RENDER
  ========================= */

  return (
    <div>
      <AdminListToolkit
        title="Products"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        extraButtons={[
          {
            label: "Price Updater",
            onClick: openPriceUpdater,
          },
        ]}
        onAdd={() => {
          setModalMode("add");

          const newRow: any = {
            id: "",
            vendor_id: "",
            region_id: "",
            region_name: "",
            SKU: "",
            barCode: "",
            product_condition: "new",
            productName: "",
            shortDescription: "",
            categoryId: "",
            subcategoryId: "",
            productType: "",
            brand_id: "",
            discountVoucherIds: [],
            colorVariants: [{ color: "", images: [null, null, null] }],
            model: "",
            cost: "",
            actualPrice: "",
            previousPrice: "",
            sellingPrice: "",
            activePrice: "selling",
            is_featured: 0,
            is_promoted: 0,
            longDescription: "",
            productVideo: "",
            stock_status: "in-stock",
            status: "active",
          };

          // ✅ If logged-in user is Vendor, auto-assign vendor + region
          if (isVendorUser) {
            const matchedVendor = vendors.find(
              (v) => String(v.id) === String(currentUser?.id),
            );

            newRow.vendor_id = currentUser?.id || "";
            newRow.region_id = matchedVendor?.region_id || "";
            newRow.region_name = matchedVendor?.region_name || "";
          }

          setActiveRow(newRow);
          setSelectedCategoryId("");
          setSubcategories([]);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          const preparedRow = enrichProductRow(row);
          setActiveRow(preparedRow);
          setSelectedCategoryId(preparedRow.categoryId || "");
          setModalOpen(true);
          fetchSubcategoriesByCategory(preparedRow.categoryId);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          const preparedRow = enrichProductRow(row);
          setActiveRow(preparedRow);
          setSelectedCategoryId(preparedRow.categoryId || "");
          setModalOpen(true);
          fetchSubcategoriesByCategory(preparedRow.categoryId);
        }}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="products"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      {modalOpen && (
        <AdminDataModal
          isOpen={modalOpen}
          mode={modalMode}
          title="Product"
          fields={productFields}
          rowData={activeRow}
          onClose={() => {
            setModalOpen(false);
            setActiveRow(null);
          }}
          onSubmit={handleSubmit}
          size="xl"
          onFormChange={handleProductFormChange}
        />
      )}

      {priceUpdaterOpen && (
        <div className="price-updater-overlay">
          <div className="price-updater-modal">
            <div className="price-updater-header">
              <div>
                <h2>Price Updater</h2>
                <p>
                  Update product cost quickly. Actual, previous, and selling
                  prices will be recalculated automatically.
                </p>
              </div>

              <button
                type="button"
                className="price-updater-close"
                onClick={closePriceUpdater}
              >
                ×
              </button>
            </div>

            <div className="price-updater-table-wrap">
              <DataTable
                columns={priceUpdaterColumns}
                data={priceUpdaterRows}
                onFilterChange={handlePriceUpdaterFilterChange}
              />
            </div>

            <div className="price-updater-footer">
              <button
                type="button"
                className="price-updater-cancel-btn"
                onClick={closePriceUpdater}
              >
                Cancel
              </button>

              <button
                type="button"
                className="price-updater-save-btn"
                onClick={savePriceUpdater}
                disabled={priceUpdaterSaving}
              >
                {priceUpdaterSaving
                  ? "Saving..."
                  : isPriceUpdaterFiltered
                    ? `Save Filtered (${priceUpdaterFilteredRows.length})`
                    : `Save All (${priceUpdaterRows.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
